// ============================================================================
// design-spec.js — the design, described in the words a HUMAN reads
// ============================================================================
// Ryan 2026-08-27, reviewing WEIGHT-1008-A's approval sheet: the Product field said
// "—", the typeface said "montserrat2", and the swatches were labelled with abstract
// palette roles ("Primary", "Secondary") rather than what the colour is actually ON.
//
// Every one of those is the same fault: **the WORKER was deriving, from the raw
// profile, an answer the APP already knows.** It has no `products.js`, no font
// manifest, and no colour model, so each answer was either a fallback
// (`clean(p.layoutPreset)` → "—") or a raw internal id.
//
// This module is the one place that turns a design into display strings, and the
// PDF payload carries them. The worker goes back to being a renderer: it lays out
// what it is given and derives nothing about the design. Same rule as CLAUDE.md
// §0b — when two places need the same answer, one of them will be wrong, and the
// one without the vocabulary is always the one that's wrong.
//
// ⚠ USED BY BOTH SHEETS. The customer's approval sheet and the supplier's vendor
// ordering sheet ask for exactly the same facts and differ only in what they SHOW,
// so the derivation lives here once and each document picks from it.
// ============================================================================

import manifest from './fonts/manifest.js';
import { normalizeHex, resolvePaletteRef, shownLogoTones, colorsConflict, contrastPoolFrom, effectiveTextStyle, logoGapFromStructure, DEFAULT_BODY_COLOR } from './design-profile.js';
import { bandsVisibleOnPlate, presentedLogoStack, logoEdgeWrapFor } from './logo-outline.js';
import { categoryLabelOne, parseProductKey, productKey, productSupportsBodyColor } from './products.js';

// ── the product's NAME ────────────────────────────────────────────────────────

/**
 * "Round Urethane Dumbbell" — the kind, then the category in the singular.
 *
 * ⚠ THIS EXACT CONSTRUCTION ALREADY EXISTED IN `ui/project-push.js`, which now
 * imports it from here. `ui/present.js` has a THIRD copy joined with a `·` instead
 * of a space; that one is deliberately different (it is a slide caption, not a
 * name) and is left alone.
 */
export function productLabel(category, kind) {
  return [String(kind || '').trim(), categoryLabelOne(category)].filter(Boolean).join(' ');
}

/**
 * Every product this design covers, named, de-duplicated, in the order they were picked.
 *
 * Ryan 2026-08-27: *"the 'Product' field… should say the product name. If there are
 * multiple, it should list them all separated by commas."* The list comes from the
 * picked SET rather than from whichever plate happens to be on the stage — a buyer
 * who designed dumbbells and fixed barbells is designing both, and a sheet naming
 * only the one they were last looking at is wrong on the other.
 *
 * ⚠ `instance` is NOT part of the name. Two configurations of one bumper are two
 * plate COLOURS of the same product; the colours are already on the sheet, and
 * printing "Competition Urethane Bumper Plate" twice would read as two line items.
 */
export function productNames(picks) {
  const out = [];
  for (const p of Array.isArray(picks) ? picks : []) {
    if (!p || !p.category) continue;
    const name = productLabel(p.category, p.kind);
    if (name && !out.includes(name)) out.push(name);
  }
  return out;
}

// ── the FONT ──────────────────────────────────────────────────────────────────

const FONT_BY_ID = new Map((Array.isArray(manifest) ? manifest : []).map((f) => [f.id, f]));

/**
 * What the buyer saw in the picker — "Montserrat", not `montserrat2`.
 *
 * ⚠ THE ID IS NOT A NAME, and the sheet printed it for a month because the worker
 * had no manifest to look it up in. Several ids are actively misleading rather than
 * merely ugly: `montserrat2` is Montserrat, and `graduate` is "College All Star"
 * (the id is frozen because it keys every saved design and four generated metric
 * tables — see FONT-RULES.md).
 */
export function fontLabel(fontId) {
  const f = FONT_BY_ID.get(String(fontId || ''));
  return (f && (f.label || f.css)) || String(fontId || '');
}

/**
 * Where a supplier gets the actual typeface — Ryan 2026-08-27: *"we'll just need to
 * make it clear where to download the font on the vendor PDF."*
 *
 * ⛔ THE LICENCE, NOT THE SOURCE, IS WHAT MAKES A FACE USABLE, and the library is
 * NOT uniformly Google Fonts (FONT-LICENSES.md). Three answers, in the order they
 * are checked:
 *
 *   1. `graduate` — "College All Star" is Graduate by Eduardo Tunni **modified by
 *      Blue Sky** (the decorative centre mark deleted from the zero). Sending a
 *      supplier to Google's Graduate would hand them a DIFFERENT ZERO from the one
 *      on the plate they are matching, so this one has to be our own file.
 *   2. the four **College** cuts — Matthew Welch, MIT, not on Google Fonts at all.
 *   3. everything else — Google Fonts, OFL 1.1 or Apache 2.0, linked to the
 *      specimen page because that is where the installable TTF family is. A woff2
 *      is what we render from; it is not what a print shop can install.
 *
 * The `css` name is the family name Google knows it by, which is why the URL is
 * derived from that rather than from the label (we rename faces; Google does not).
 */
export const WD_FONT_HOST = 'https://weight-designer.pages.dev/fonts';

const COLLEGE_MIT = 'https://squaregear.net/fonts/college.html';

const FONT_SOURCE_EXCEPTIONS = Object.freeze({
  graduate: { url: `${WD_FONT_HOST}/graduate.woff2`, note: 'Blue Sky cut — use this file, not Google’s Graduate' },
  college: { url: COLLEGE_MIT, note: 'Matthew Welch, MIT' },
  collegebold: { url: COLLEGE_MIT, note: 'Matthew Welch, MIT' },
  collegecond: { url: COLLEGE_MIT, note: 'Matthew Welch, MIT' },
  collegesemi: { url: COLLEGE_MIT, note: 'Matthew Welch, MIT' },
});

export function fontSource(fontId) {
  const id = String(fontId || '');
  const exception = FONT_SOURCE_EXCEPTIONS[id];
  if (exception) return { ...exception };
  const f = FONT_BY_ID.get(id);
  if (!f) return null;
  const family = String(f.css || f.label || '').trim();
  if (!family) return null;
  return {
    url: `https://fonts.google.com/specimen/${family.replace(/\s+/g, '+')}`,
    note: 'Google Fonts',
  };
}

// ── the COLOURS, and what each one is ON ──────────────────────────────────────

/* Ryan 2026-08-27: *"include sub-text under those hex codes for what each color is
   used on… they'll always say 'Logo' under the hex code first and foremost (unless
   they don't use a logo), then under that we can include stuff like 'Text fill',
   'Text outline'."*

   ⚠ USAGE, NOT ROLE. The sheet used to print `palette.primary`/`secondary`/
   `tertiary` as "Primary"/"Secondary"/"Tertiary". Those are the slots a colour is
   PARKED in, not jobs it does — a palette slot that nothing wears was still being
   printed, and a per-piece recolour that wore a literal hex was not. So nothing
   here reads the palette directly: every entry is produced by something on the
   plate, and a palette ref is resolved THROUGH the palette to the hex it means.

   ⚠ INTENT-LEVEL, deliberately. `logo-outline.js` has a whole contextual view of
   what a band does on a given plate (peel, repaint, keep). That is a RENDERING
   answer and re-deriving it here would be a fourth copy of it; what a printer needs
   is the colour the design SAYS, which is what these fields hold. The plate image
   on the sheet shows the rest, and the sheet says so. */

/* Ryan's order within one swatch: the mark leads, then the lettering. An outline may carry a ring
   ordinal ("Text outline 2 (outer)"), so the rank is matched on the PREFIX rather than the whole
   string — an exact-match list silently sorted every numbered ring last. */
const USE_ORDER = ['Logo', 'Text fill', 'Text outline'];
const useRank = (u) => {
  const i = USE_ORDER.findIndex((k) => String(u).startsWith(k));
  return i === -1 ? 99 : i;
};

/**
 * What the BODY of this product is called, for the one case where its colour is worth printing.
 *
 * ⛔ IT SAID "Plate" ON A DUMBBELL (Ryan 2026-08-27: *"this design was for a dumbbell so that's
 * wrong"*). The label was a constant left over from a tool that only made bumper plates.
 */
function bodyUse(picks, product) {
  const first = (Array.isArray(picks) && picks[0]) || null;
  const category = (first && first.category) || parseProductKey(product || '').category;
  return category ? categoryLabelOne(category) : 'Body';
}

/**
 * Is the design's body colour one the factory could actually have poured?
 *
 * ⛔ THE COLOUR ALONE IS NOT ENOUGH OF A TEST, since 2026-08-31. A saved design keeps the
 * `bodyColor` it was designed with, and `applySnapshot` (`ui/state.js`) restores it verbatim
 * without sanitising it against the product. So when a product STOPS being dyeable, every design
 * already saved against it still carries a live-looking hex. The screen handles that (the renderer
 * forces black and the panel drops the row), but this file feeds the VENDOR DESIGN SHEET, and a
 * stale hex there is a colour instruction to the factory for a plate they cannot make in it.
 * That is exactly what happened when the competition RUBBER plate lost colour: rubber is compounded
 * black, only urethane is poured to colour.
 *
 * ANY picked product being dyeable is enough, because the profile carries one body colour for the
 * design and `bodyUse` attributes it to the first pick.
 */
function bodyIsDyeable(picks, product) {
  const keys = (Array.isArray(picks) ? picks : [])
    .filter((k) => k && k.category && k.kind)
    .map((k) => productKey(k.category, k.kind, k.instance));
  if (product) keys.push(product);
  return keys.some((k) => productSupportsBodyColor(k));
}

/**
 * Every colour the design WEARS, with the job each one does.
 *
 * ⛔ THE DEFAULT BODY COLOUR IS NOT A DESIGN DECISION and is never listed (Ryan 2026-08-27: *"these
 * dumbbells are just black rubber first of all… we don't need to print the default color on these
 * sheets, everyone knows they're black including the vendor"*). It also printed as `#0B0C0F`, which
 * is the near-black the renderer uses and reads as a specification when it is really "unpainted".
 * Only a body somebody deliberately DYED earns a swatch.
 *
 * ⚠ THIS USED TO SAY "only dyeable products can be dyed, so the test is the colour, not the
 * category". That was true until a product lost its dye. It now takes BOTH tests: a non-default
 * colour AND a product that can still wear one. See `bodyIsDyeable`.
 */
export function designColorUsage(state) {
  const p = (state && state.designProfile) || {};
  const pal = p.palette || {};
  const picks = (state && state.ui && state.ui.selectedProducts) || [];
  const out = [];

  const add = (use, value) => {
    const hex = normalizeHex(value);
    if (!hex) return;
    const seen = out.find((c) => c.hex === hex);
    if (seen) { if (!seen.uses.includes(use)) seen.uses.push(use); return; }
    out.push({ hex, uses: [use] });
  };

  const body = normalizeHex(p.bodyColor);
  if (body && body !== normalizeHex(DEFAULT_BODY_COLOR)
      && bodyIsDyeable(picks, state && state.ui && state.ui.product)) {
    add(bodyUse(picks, state && state.ui && state.ui.product), body);
  }

  const logoEl = (p.elements || []).find((e) => e && e.type === 'logo');
  if (logoEl) {
    /* ⛔ THE MARK'S OWN TONES COME FIRST, AND THEY ARE THE THING THE OLD VERSION COULD NOT SEE.
       Ryan 2026-08-27: *"It's giving the color 802740 for the text fill but not for the logo. but I
       can clearly see a red color on the logo just like it."* Exactly right, and the cause was that
       every source read here was a field that only exists once the mark has been ANALYSED into
       bands or hand-recoloured. A logo rendering in its original artwork colours has none of them
       set — its tones live in the raster — so it contributed no `Logo` usage at all, while the
       lettering wore the same red and was reported, because the palette is seeded FROM the mark.
         `shownLogoTones` is the fix and is not a new rule: it is the exact list of chips the buyer
       was shown on the Add Logo tab, and its own docstring is about two surfaces disagreeing over
       "the logo's colours". A third surface making up a third answer is the same bug again. */
    const swaps = new Map((logoEl.logoToneSwaps || [])
      .map((sw) => [normalizeHex(sw && sw.from), normalizeHex(sw && sw.to)])
      .filter(([f, t]) => f && t));
    for (const tone of shownLogoTones((state && state.ui && state.ui.palette) || null)) {
      // A repainted tone reports what it BECAME. The `from` is history and is nowhere on the plate.
      add('Logo', swaps.get(tone) || tone);
    }
    for (const to of swaps.values()) add('Logo', to);

    const ls = p.logoStructure;
    const bands = ls && Array.isArray(ls.bands) ? ls.bands : [];
    const overrides = logoEl.logoBandColors || {};
    bands.forEach((band, i) => add('Logo', overrides[i] || band));
    for (const k of Object.keys(overrides)) if (!bands[k | 0]) add('Logo', overrides[k]);
    add('Logo', logoEl.logoCenterColor);
    for (const ring of logoEl.logoAddOutline || []) add('Logo', ring && ring.color);
    if (!bands.length && ls) add('Logo', ls.fillHex);

    /* ⛔ THE ENCLOSED POCKETS ARE PRINTED INK WHEN SHOW-THROUGH IS OFF (Ryan 2026-08-27: *"we
       checked 'No' for let weight show through enclosed areas. and that part shining through is
       white."*). `keyGlobal === false` means the pockets were NOT cut, so they keep the keyed
       field's colour — `keyPockets.pocketHex`, measured at upload and serialized for exactly this
       kind of question. That is a colour the factory has to lay down, and it appeared on no list:
       it is not a palette role, not a band, and not a tone of the artwork the extractor reports. */
    const lg = logoEl.logo || {};
    if (lg.keyGlobal === false && lg.keyPockets) add('Logo', lg.keyPockets.pocketHex);
  }

  for (const e of p.elements || []) {
    if (!e || (e.type !== 'arctext' && e.type !== 'weight')) continue;
    add('Text fill', resolvePaletteRef(e.fillRef || 'primary', pal));
    /* ⛔ A DISABLED RING IS IN THE STACK AND IS NOT PAINTED (Ryan 2026-08-27: *"the dumbbell clearly
       has a solid fill color and a single outline around it… but the COLORS section shows FCC529 and
       FDD175, both with 'Text outline' under them"*). The on/off toggle GRAYS a ring rather than
       deleting it, so the buyer can put it back — `serializeOutline` keeps `enabled: false` on the
       wire and `effectiveTextStyle` filters it out of the render stack. This walked the raw array
       and reported a colour that is nowhere on the plate.
         On a sheet somebody manufactures from, that is not a cosmetic slip: it is an instruction to
       print a second ring the customer never approved. */
    /* ⚠ AN OUTLINE IS A STACK, AND THE SHEET HAS TO SAY WHICH RING IS WHICH (Ryan 2026-08-27).
       He read two "Text outline" rows on a design he saw one outline on, and the sheet was right:
       `rpqiF1NL` carries a weight number with TWO rings — an inner 1-unit #FDD175 and an outer
       1.5-unit #FCC529 — which at that size, in two yellows, on a near-black plate read as one
       2.5-unit yellow band. Two rows with the identical label gave him no way to tell a two-ring
       stack from two unrelated colours, and a factory reading it has the same problem.
         Numbered from the glyph outward, which is the order the array is stored and drawn in. A
       single ring stays plain "Text outline" — an ordinal on a stack of one is noise. */
    /* ⛔ THE **EFFECTIVE** STACK, NOT THE STORED ONE (2026-09-16, the mirror rule). The sheet is
       what somebody manufactures from, and since the mirror the number can wear a ring that is
       nowhere in `e.outlines`: it is derived per plate from the mark's own outermost outline, the
       same way the plate-conflict peel has always been. Walking the stored array under-reported a
       ring the factory has to lay down — the exact inverse of the disabled-ring bug this block was
       written for, and the same kind of lie.
         ⚠ THE GAP MOAT IS EXCLUDED, and by its own definition rather than by a special case: it is
       painted in the PLATE's colour (`withGap` uses `bodyColor` as its `colorRef`), which is not
       ink at all — it is the rubber showing through, exactly as the mark's own keyed gap does. */
    const specStack = presentedLogoStack(p, body);
    const specOuter = specStack.length ? specStack[0] : null;
    const specPalette = {
      ...pal,
      pool: contrastPoolFrom(pal, p.swatches),
      logoOutlined: !!(p.logoStructure && Array.isArray(p.logoStructure.bands) && p.logoStructure.bands.length),
      logoGap: logoGapFromStructure(p.logoStructure),
      logoBandsVisible: bandsVisibleOnPlate(p, body),
      logoOuterTone: specOuter ? specOuter.hex : null,
      logoOuterWeight: specOuter ? specOuter.weight : 0,
      logoStack: specStack.map((b) => ({ hex: b.hex, weight: b.weight })),
      logoEdgeWrap: logoEdgeWrapFor(p, body),
    };
    const eff = effectiveTextStyle(e, specPalette, body);
    const rings = (eff.outlines || [])
      .map((o) => normalizeHex(resolvePaletteRef(o.colorRef, specPalette)))
      .filter((hex) => hex && !(body && colorsConflict(body, hex)));
    rings.forEach((hex, i) => {
      const where = rings.length > 1 ? ` ${i + 1} (${i === 0 ? 'inner' : 'outer'})` : '';
      add(`Text outline${where}`, hex);
    });
  }

  /* Ryan's order, applied WITHIN a swatch: a colour that is both the mark's and the lettering's
     leads with the mark. The body colour, when it is present at all, is its own product word and
     sorts last by falling outside the list. */
  for (const c of out) c.uses.sort((a, b) => useRank(a) - useRank(b));
  return out;
}

// ── the whole thing, for the wire ─────────────────────────────────────────────

/**
 * Everything a PDF needs that only the app can answer. Sent alongside the share id,
 * which still carries the design itself — the worker reads the profile from KV for
 * the text ON the plate and the picture, exactly as it did, and takes these as the
 * names for things it cannot name.
 */
export function designSpec(state, { customerName = '' } = {}) {
  const p = (state && state.designProfile) || {};
  const picks = (state && state.ui && state.ui.selectedProducts) || [];
  const fontId = p.font || '';
  const products = productNames(picks);
  /* WHAT THE DOCUMENT IS ABOUT, decided HERE because this is the only place that knows both halves
     — the name the rep typed and how many products the design covers.
     Ryan 2026-08-27: *"If a name is not entered… the cover page should not even say 'PROPOSAL FOR:'
     and then you can use the generic… CUSTOM WEIGHT DESIGN if we're sending them 1, or CUSTOM
     WEIGHT DESIGNS if there's multiple."*
     The cover's title, the band on every page and the download filename all take this one string;
     three answers to "whose design is this" would be three chances to disagree. */
  const named = String(customerName || p.orgName || '').trim();
  return {
    products,
    named,
    heading: named || `Custom Weight Design${products.length > 1 ? 's' : ''}`,
    fontId,
    fontLabel: fontLabel(fontId),
    fontSource: fontSource(fontId),
    colors: designColorUsage(state),
    /* Spelled out rather than abbreviated (Ryan 2026-08-27) — the customer's sheet no longer prints
       the denomination, so this is the only place the unit is stated, and "lb" on a document
       going overseas is worth two more syllables. */
    unit: ((state && state.ui && state.ui.unit) === 'kg' || p.unit === 'kg') ? 'Kilograms' : 'Pounds',
  };
}
